import { ArrowUpFromLine, Check, Eye, EyeOff, Landmark, ShieldCheck, X } from "lucide-react";
import { cloneElement, useState, type FormEvent, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { AutofillUsername } from "./AutofillUsername";
import { apiRequest } from "../lib/api";
import { bankNameFromClabe, formatClabe, isValidClabe, maskClabe, sanitizeClabe } from "../lib/clabe";
import { registeredSpeiAccount, type ApiEnvelope, type SpeiAccount, type User, type WalletTransaction } from "../types";
import { Spinner } from "./Spinner";
import { Modal } from "./Modal";
import { ButtonLabel } from "./ButtonLabel";

const PRESETS = [500, 1000, 2500, 5000];
const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

/**
 * The SPEI withdrawal journey: destination account → amount → review → receipt.
 *
 * The destination is always one of the profile's registered SPEI accounts (up to five); the
 * customer picks which one when there is more than one. When none is registered yet, the dialog
 * opens on the registration step with an EMPTY identifier field — the account number is never
 * prefilled from the e-mail or any other profile field, and only a value that passes the
 * 18-digit + check-digit validation can be saved or used.
 */
type Step = "loading" | "account" | "amount" | "review" | "done";

interface Props {
  walletId: string;
  maxAmount: number;
  onCompleted(transaction: WalletTransaction): void;
  trigger: ReactElement<{ onClick?: () => void }>;
}

interface AccountsPayload {
  accounts: SpeiAccount[];
  user?: User;
  account?: SpeiAccount;
}

export function SpeiWithdrawalDialog({ walletId, maxAmount, onCompleted, trigger }: Props) {
  const { user, applyUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("loading");
  const [accounts, setAccounts] = useState<SpeiAccount[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clabeInput, setClabeInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [amount, setAmount] = useState("");
  const [concept, setConcept] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WalletTransaction | null>(null);

  // An account created through Google or Apple has no password yet, and step-up requires one.
  const needsPassword = user?.hasPassword === false;
  const selected = accounts.find((account) => account.id === selectedId) ?? null;
  const registered = registeredSpeiAccount(accounts);

  const reset = () => {
    setStep("loading"); setAccounts([]); setSelectedId(null);
    setClabeInput(""); setLabelInput(""); setAmount(""); setConcept(""); setPassword("");
    setShowPassword(false); setError(null); setResult(null);
  };

  const openDialog = () => {
    reset();
    setOpen(true);
    void apiRequest<ApiEnvelope<{ accounts: SpeiAccount[] }>>("/api/v1/profile/spei-accounts")
      .then((response) => {
        const list = response.data.accounts;
        setAccounts(list);
        // Withdrawals only leave to the account registered with Kuspit; the default is a fallback
        // for the copy shown when nothing is registered yet.
        const preferred = registeredSpeiAccount(list) ?? list.find((account) => account.isDefault) ?? list[0] ?? null;
        setSelectedId(preferred?.id ?? null);
        setStep(list.length ? "amount" : "account");
      })
      .catch((cause) => {
        setError(cause instanceof Error ? cause.message : "No pudimos cargar tus cuentas SPEI.");
        setStep("account");
      });
  };

  /** Creates the first password when needed, then trades the password for a step-up token. */
  const obtainStepUp = async (): Promise<string> => {
    if (needsPassword) {
      const created = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/password/set", {
        method: "POST", body: JSON.stringify({ password })
      });
      applyUser(created.data.user);
    }
    const stepUp = await apiRequest<ApiEnvelope<{ stepUpToken: string }>>("/api/v1/auth/step-up", {
      method: "POST", body: JSON.stringify({ password })
    });
    return stepUp.data.stepUpToken;
  };

  const saveAccount = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!isValidClabe(clabeInput)) {
      setError("La CLABE debe tener 18 dígitos y un dígito de control válido.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await obtainStepUp();
      const saved = await apiRequest<ApiEnvelope<AccountsPayload>>("/api/v1/profile/spei-accounts", {
        method: "POST",
        headers: { "x-step-up-token": token },
        body: JSON.stringify({
          clabe: clabeInput,
          ...(labelInput.trim() ? { label: labelInput.trim() } : {}),
          makeDefault: accounts.length === 0
        })
      });
      if (saved.data.user) applyUser(saved.data.user);
      setAccounts(saved.data.accounts);
      setSelectedId(saved.data.account?.id ?? saved.data.accounts[0]?.id ?? null);
      setClabeInput(""); setLabelInput("");
      setStep("amount");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No pudimos guardar tu cuenta SPEI.");
    } finally {
      setSubmitting(false);
    }
  };

  const toReview = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!selected) { setError("Elige la cuenta de destino."); return; }
    if (!registered) { setError("Registra tu cuenta en Kuspit desde tu perfil antes de retirar."); return; }
    if (selected.id !== registered.id) { setError("Los retiros solo pueden enviarse a la cuenta registrada en Kuspit."); return; }
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) { setError("Ingresa un monto válido."); return; }
    if (Math.round(numericAmount * 100) !== numericAmount * 100) { setError("Usa máximo dos decimales."); return; }
    if (numericAmount > maxAmount) { setError("El monto supera tu saldo disponible."); return; }
    setStep("review");
  };

  const confirmWithdrawal = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    setError(null);
    setSubmitting(true);
    try {
      const token = await obtainStepUp();
      const response = await apiRequest<ApiEnvelope<{ transaction: WalletTransaction }>>(
        `/api/v1/wallets/${walletId}/withdrawals`,
        {
          method: "POST",
          headers: { "idempotency-key": crypto.randomUUID(), "x-step-up-token": token },
          body: JSON.stringify({
            amount: Number(amount),
            speiAccountId: selected.id,
            ...(concept.trim() ? { concept: concept.trim() } : {})
          })
        }
      );
      setResult(response.data.transaction);
      setStep("done");
      onCompleted(response.data.transaction);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No pudimos enviar el retiro.");
    } finally {
      setSubmitting(false);
    }
  };

  const accountTitle = (account: SpeiAccount) =>
    account.label?.trim() || bankNameFromClabe(account.clabe) || "Cuenta bancaria";

  const passwordField = (id: string) => (
    <>
      <label htmlFor={id}>{needsPassword ? "Crea una contraseña para confirmar" : "Confirma tu contraseña"}</label>
      <div className="password-field">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete={needsPassword ? "new-password" : "current-password"}
          minLength={needsPassword ? 12 : undefined}
          required
        />
        <button type="button" className="field-icon" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
          {showPassword ? <EyeOff size={19} aria-hidden="true" /> : <Eye size={19} aria-hidden="true" />}
        </button>
      </div>
      {needsPassword && (
        <span className="amount-hint">
          Tu cuenta se creó con Apple o Google. Las operaciones financieras necesitan una
          contraseña: 12 caracteres, mayúscula, minúscula, número y símbolo.
        </span>
      )}
    </>
  );

  return (
    <>
      {cloneElement(trigger, { onClick: openDialog })}
      <Modal open={open} label="Retiro SPEI" onOpenChange={(next) => { setOpen(next); if (!next) reset(); }}>
        <div className="dialog-content" aria-describedby="spei-description">
          {step === "loading" && (
            <div className="dialog-loading"><Spinner label="Cargando cuentas SPEI" /><span>Cargando tus cuentas…</span></div>
          )}

          {step === "account" && (
            <>
              <div className="dialog-icon secure"><ShieldCheck aria-hidden="true" /></div>
              <span className="page-eyebrow">Retiro SPEI</span>
              <h2>{accounts.length ? "Agrega otra cuenta SPEI" : "Registra tu cuenta SPEI"}</h2>
              <p id="spei-description">
                Tus retiros se envían por SPEI a una CLABE de 18 dígitos. Puedes registrar hasta 5 cuentas.
              </p>
              <form className="dialog-form" onSubmit={saveAccount}>
                <AutofillUsername />
                <label htmlFor="spei-clabe">CLABE (18 dígitos)</label>
                <div className="clabe-field">
                  <input
                    id="spei-clabe"
                    className="clabe-input"
                    translate="no"
                    inputMode="numeric"
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-form-type="other"
                    name="spei-clabe"
                    placeholder="000 000 00000000000 0"
                    value={formatClabe(clabeInput)}
                    onChange={(event) => setClabeInput(sanitizeClabe(event.target.value))}
                    aria-describedby="spei-clabe-hint"
                    autoFocus
                    required
                  />
                </div>
                <span className="amount-hint" id="spei-clabe-hint">
                  {clabeInput.length === 18
                    ? isValidClabe(clabeInput)
                      ? <span className="clabe-valid"><Check size={13} aria-hidden="true" /> {bankNameFromClabe(clabeInput) ?? "Banco"} · CLABE válida</span>
                      : "El dígito de control no coincide. Revisa el número."
                    : `${clabeInput.length}/18 dígitos`}
                </span>
                <label htmlFor="spei-label">Alias <span className="label-optional">(opcional)</span></label>
                <input
                  id="spei-label"
                  className="standalone-input"
                  value={labelInput}
                  onChange={(event) => setLabelInput(event.target.value)}
                  maxLength={40}
                  autoComplete="off"
                  placeholder="Mi cuenta de nómina"
                />
                {passwordField("spei-account-password")}
                {error && <div className="form-error" role="alert">{error}</div>}
                <button className="primary-button full" type="submit" disabled={submitting || !isValidClabe(clabeInput)}>
                  <ButtonLabel busy={submitting} busyLabel="Guardando" label="Guardar cuenta y continuar" />
                </button>
                {accounts.length > 0 && (
                  <button type="button" className="link-button dialog-secondary-action" onClick={() => { setStep("amount"); setError(null); }}>
                    Regresar
                  </button>
                )}
              </form>
            </>
          )}

          {step === "amount" && (
            <>
              <div className="dialog-icon withdrawal"><ArrowUpFromLine aria-hidden="true" /></div>
              <span className="page-eyebrow">Retiro SPEI</span>
              <h2>Retirar fondos</h2>
              <p id="spei-description">Disponible: {money.format(maxAmount)}</p>
              <form className="dialog-form" onSubmit={toReview}>
                <span className="field-label">Cuenta de destino</span>
                {!registered && (
                  <div className="form-error" role="alert">
                    Kuspit envía los retiros únicamente a la cuenta registrada en tu contrato. Aún no tienes una:
                    regístrala desde <Link to="/perfil">tu perfil</Link> subiendo la carátula de tu cuenta. La autorización tarda minutos y como máximo 72 horas.
                  </div>
                )}
                <div className="destination-list" role="radiogroup" aria-label="Cuenta de destino">
                  {(registered ? [registered] : accounts).map((account) => (
                    <label key={account.id} className={`destination-card selectable${account.id === selectedId ? " selected" : ""}`}>
                      <input
                        type="radio"
                        name="spei-destination"
                        checked={account.id === selectedId}
                        onChange={() => setSelectedId(account.id)}
                        disabled={!registered}
                      />
                      <span className="destination-icon"><Landmark size={19} aria-hidden="true" /></span>
                      <div>
                        <strong>
                          {accountTitle(account)}
                          {account.kuspit.status === "authorized" && <em className="default-chip">Autorizada</em>}
                          {account.kuspit.status === "pending" && <em className="default-chip">En revisión</em>}
                        </strong>
                        <span>CLABE <span translate="no">{maskClabe(account.clabe)}</span> · {bankNameFromClabe(account.clabe) ?? "SPEI"}</span>
                      </div>
                      {account.id === selectedId && <Check size={17} aria-hidden="true" className="destination-check" />}
                    </label>
                  ))}
                </div>
                {registered?.kuspit.status === "pending" && (
                  <span className="amount-hint">
                    Kuspit todavía está revisando esta cuenta. Si el retiro es rechazado por falta de autorización, inténtalo más tarde: suele tardar minutos y como máximo 72 horas.
                  </span>
                )}
                {accounts.length < 5 && !registered && (
                  <button type="button" className="link-button destination-add" onClick={() => { setStep("account"); setError(null); }}>
                    + Agregar otra cuenta
                  </button>
                )}
                <div className="amount-display">
                  <label className="field-label" htmlFor="spei-amount">Monto</label>
                  <div className="money-field">
                    <span aria-hidden="true">$</span>
                    <input
                      id="spei-amount"
                      name="spei-amount"
                      inputMode="decimal"
                      autoComplete="transaction-amount"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                      placeholder="0"
                      aria-describedby="spei-currency"
                      required
                    />
                    <span id="spei-currency">MXN</span>
                  </div>
                  <span className="amount-hint">Sin comisión · llega a tu banco vía SPEI.</span>
                </div>
                <div className="preset-grid" role="group" aria-label="Cantidades sugeridas">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`preset-chip${Number(amount) === preset ? " active" : ""}`}
                      aria-pressed={Number(amount) === preset}
                      disabled={preset > maxAmount}
                      onClick={() => setAmount(String(preset))}
                    >
                      ${preset.toLocaleString("es-MX")}
                    </button>
                  ))}
                </div>
                <label htmlFor="spei-concept">Concepto <span className="label-optional">(opcional)</span></label>
                <input
                  id="spei-concept"
                  className="standalone-input"
                  value={concept}
                  onChange={(event) => setConcept(event.target.value)}
                  maxLength={80}
                  autoComplete="off"
                  placeholder="Retiro Aventuria"
                />
                {error && <div className="form-error" role="alert">{error}</div>}
                <button className="primary-button full" type="submit" disabled={!registered}>Continuar</button>
              </form>
            </>
          )}

          {step === "review" && selected && (
            <>
              <div className="dialog-icon withdrawal"><ArrowUpFromLine aria-hidden="true" /></div>
              <span className="page-eyebrow">Retiro SPEI · Confirmación</span>
              <h2>Revisa tu retiro</h2>
              <p id="spei-description">Verifica los datos antes de confirmar. La operación no se puede revertir.</p>
              <form className="dialog-form" onSubmit={confirmWithdrawal}>
                <AutofillUsername />
                <dl className="review-list">
                  <div><dt>Monto</dt><dd>{money.format(Number(amount))}</dd></div>
                  <div><dt>Comisión</dt><dd>{money.format(0)}</dd></div>
                  <div><dt>Destino</dt><dd>{accountTitle(selected)} · <span translate="no">{maskClabe(selected.clabe)}</span></dd></div>
                  <div><dt>Vía</dt><dd>Transferencia SPEI</dd></div>
                  <div><dt>Concepto</dt><dd>{concept.trim() || "Retiro Aventuria"}</dd></div>
                  <div className="review-total"><dt>Recibes</dt><dd>{money.format(Number(amount))}</dd></div>
                </dl>
                {passwordField("spei-review-password")}
                {error && <div className="form-error" role="alert">{error}</div>}
                <button className="primary-button full" type="submit" disabled={submitting}>
                  <ButtonLabel busy={submitting} busyLabel="Enviando" label="Confirmar retiro" />
                </button>
                <button type="button" className="link-button dialog-secondary-action" onClick={() => { setStep("amount"); setError(null); }}>
                  Regresar
                </button>
              </form>
            </>
          )}

          {step === "done" && result && selected && (
            <>
              <div className="dialog-icon deposit"><Check aria-hidden="true" /></div>
              <span className="page-eyebrow">Retiro SPEI</span>
              <h2>{result.status === "settled" ? "Retiro confirmado" : "Retiro en proceso"}</h2>
              <p id="spei-description">
                {result.status === "settled"
                  ? "Tu transferencia SPEI fue enviada a tu banco."
                  : "Tu retiro fue registrado y se confirmará en cuanto el banco lo procese."}
              </p>
              <dl className="review-list">
                <div><dt>Monto</dt><dd translate="no">{money.format(result.amount)}</dd></div>
                <div><dt>Destino</dt><dd>{accountTitle(selected)} · <span translate="no">{maskClabe(selected.clabe)}</span></dd></div>
                {result.trackingKey && <div><dt>Clave de rastreo</dt><dd className="tracking-key" translate="no">{result.trackingKey}</dd></div>}
              </dl>
              <span className="amount-hint">Guarda la clave de rastreo: identifica tu transferencia ante cualquier banco.</span>
              <div className="dialog-form">
                <button className="primary-button full" type="button" onClick={() => { setOpen(false); reset(); }}>Listo</button>
              </div>
            </>
          )}

          <button type="button" className="dialog-close" onClick={() => { setOpen(false); reset(); }} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        </div>
      </Modal>
    </>
  );
}
